/**
 * 🟩 P26.11 / 🟩 P26.32 / 🟥 P26.38 · buildRenderableCode
 *
 * Convierte un trabajo multi-archivo del Workspace en UN solo código
 * renderizable (HTML combinado o bundle JSX con el CSS inyectado).
 * 🟥 P26.40 · Extraído a `utils/` para que el panel, la vista previa y el
 * deploy real publiquen EXACTAMENTE lo mismo que se ve en la preview.
 */
export function buildRenderableCode(module) {
  if (!module) return '';
  const allFiles = Array.isArray(module.files) ? module.files : null;
  const renderMode = (module.render_mode || module.renderMode || '').toLowerCase();
  if (!allFiles || allFiles.length === 0) return module.code || '';

  const nameOf = (f) => (typeof f?.name === 'string' ? f.name.toLowerCase().replace(/^\.\//, '') : '');
  // 🟥 P26.44 · Archivos que NO son fuente de la app: si entraban al bundle el
  // preview quedaba en blanco (webpack.config.js con module.exports) o se
  // renderizaba el arranque en vez del componente.
  const isNoise = (f) => {
    const n = nameOf(f);
    return /(^|\/)(package(-lock)?\.json|tsconfig\.json|\.babelrc|readme\.md)$/.test(n)
      || /\.config\.(js|cjs|mjs|ts)$/.test(n)
      || /(^|\/)(setuptests|serviceworker|reportwebvitals)\.(js|jsx|ts|tsx)$/.test(n);
  };
  const isBootstrap = (f) => {
    const n = nameOf(f);
    const body = String(f?.content || '');
    return /(^|\/)(index|main)\.(js|jsx|tsx)$/.test(n)
      && /(ReactDOM\.render|createRoot\s*\()/.test(body)
      && /from\s+['"]\.\/App['"]/.test(body);
  };
  const files = allFiles.filter((f) => !isNoise(f) && !isBootstrap(f));
  if (files.length === 0) return module.code || '';

  const findByExt = (ext) => files.find((f) => nameOf(f).endsWith(ext));
  const hasJsxFiles = files.some((f) => /\.(jsx|tsx)$/.test(nameOf(f)));
  const hasReactSource = hasJsxFiles || renderMode === 'react';

  // Si es HTML → combinar index.html + <style>style.css</style> + <script>script.js</script>
  // 🟥 P26.44/P26.45 · Un `public/index.html` (cáscara con <div id="root">) NO
  // convierte un proyecto React en sitio HTML — ni siquiera si el módulo quedó
  // mal marcado como `render_mode: 'html'`: antes se servía la cáscara vacía y
  // la vista previa salía EN BLANCO.
  if ((renderMode === 'html' || findByExt('.html')) && !hasJsxFiles) {
    const html = (findByExt('.html') || {}).content || '';
    const css = (findByExt('.css') || {}).content || '';
    const js = (findByExt('.js') || {}).content || '';
    if (!html) {
      // Fallback: HTML mínimo envolviendo CSS/JS
      return `<!DOCTYPE html>\n<html><head><meta charset="utf-8"><style>${css}</style></head><body><script>${js}</script></body></html>`;
    }
    let out = html;
    // Inyectar CSS antes de </head> (o al principio si no hay head)
    if (css) {
      const styleTag = `<style>\n${css}\n</style>`;
      if (/<\/head>/i.test(out)) {
        out = out.replace(/<\/head>/i, `${styleTag}\n</head>`);
      } else {
        out = `${styleTag}\n${out}`;
      }
    }
    // Inyectar JS antes de </body> (o al final si no hay body)
    if (js) {
      const scriptTag = `<script>\n${js}\n</script>`;
      if (/<\/body>/i.test(out)) {
        out = out.replace(/<\/body>/i, `${scriptTag}\n</body>`);
      } else {
        out = `${out}\n${scriptTag}`;
      }
    }
    return out;
  }

  // Proyecto React multi-archivo: concatenamos los componentes auxiliares
  // en un solo módulo (el iframe usa Babel standalone y no resuelve
  // imports relativos). Los imports relativos se eliminan y los exports
  // se degradan a declaraciones globales.
  // 🟩 P26.32 · El entry se elige por NOMBRE (App.jsx/main/index) y el CSS
  // de los .css se inyecta en el documento del iframe — antes se perdía
  // (proyectos con styles.css salían sin estilos).
  const byName = (want) => files.find((f) => nameOf(f).endsWith(want));
  // 🟥 P26.38 · Entry sintético: si no hay App/main/index entre los archivos
  // pero el módulo tiene `code` raíz, ese es el entry (antes se tomaba el
  // primer .jsx y el preview mostraba una sección suelta).
  const namedEntry = byName('app.jsx') || byName('app.tsx') || byName('main.jsx') || byName('index.jsx');
  const rootCode = String(module.code || '').trim();
  const syntheticEntry = (!namedEntry && rootCode && /<[A-Za-z]/.test(rootCode))
    ? { name: '__entry.jsx', content: rootCode }
    : null;
  const main = namedEntry || syntheticEntry
    || findByExt('.jsx') || findByExt('.tsx') || findByExt('.js') || files[0];
  const reactFiles = files.filter((f) => /\.(jsx|tsx|js)$/i.test(nameOf(f)));
  const cssBundle = files
    .filter((f) => /\.css$/i.test(nameOf(f)))
    .map((f) => String(f.content || ''))
    .join('\n');
  const cssInjection = cssBundle
    ? `const __damyxCss = ${JSON.stringify(cssBundle)};\n`
      + `if (typeof document !== 'undefined' && !document.getElementById('damyx-project-css')) {\n`
      + `  const __s = document.createElement('style');\n`
      + `  __s.id = 'damyx-project-css';\n`
      + `  __s.textContent = __damyxCss;\n`
      + `  document.head.appendChild(__s);\n`
      + `}\n`
    : '';
  if ((reactFiles.length > 1 || syntheticEntry) && main) {
    const stripModuleSyntax = (src) => String(src || '')
      // imports relativos (./x, ../x) — se resuelven por concatenación
      .replace(/^\s*import\s+[^;\n]*from\s+['"]\.{1,2}\/[^'"]+['"];?\s*$/gm, '')
      .replace(/^\s*import\s+['"]\.{1,2}\/[^'"]+['"];?\s*$/gm, '')
      .replace(/^\s*export\s+default\s+function\s+/gm, 'function ')
      .replace(/^\s*export\s+default\s+class\s+/gm, 'class ')
      .replace(/^\s*export\s+default\s+([A-Za-z_$][\w$]*)\s*;?\s*$/gm, '')
      .replace(/^\s*export\s+(const|let|var|function|class)\s+/gm, '$1 ');
    const helpers = reactFiles
      .filter((f) => f.name !== main.name)
      .map((f) => `/* ${f.name} */\n${stripModuleSyntax(f.content)}`)
      .join('\n\n');
    const entrySrc = String(main.content || '');
    // Conservamos los imports de librerías (react, etc.) al principio.
    const libImports = (entrySrc.match(/^\s*import\s+[^;\n]*from\s+['"][^.'"][^'"]*['"];?\s*$/gm) || []).join('\n');
    const entryBody = stripModuleSyntax(entrySrc)
      .replace(/^\s*import\s+[^;\n]*from\s+['"][^.'"][^'"]*['"];?\s*$/gm, '');
    return `${libImports}\n\n${cssInjection}${helpers}\n\n/* ${main.name} */\n${entryBody}`;
  }
  // 🟥 P26.44 · Un solo archivo fuente: igual hay que sacar los imports
  // relativos (`import './App.css'`) o Babel del iframe falla y sale en blanco.
  const single = String((main && main.content) || module.code || '');
  const singleClean = single
    .replace(/^\s*import\s+[^;\n]*from\s+['"]\.{1,2}\/[^'"]+['"];?\s*$/gm, '')
    .replace(/^\s*import\s+['"]\.{1,2}\/[^'"]+['"];?\s*$/gm, '');
  return `${cssInjection}${singleClean}`;
}


export default buildRenderableCode;
